import React, { useEffect, useState } from 'react';
import { adminGetStats, adminGetTransactions, adminGetUsers } from '../../api/api';
import {
  Users, ShieldCheck, ShieldX, TrendingUp,
  TrendingDown, Activity, UserCheck, UserX,
  ArrowUpRight,
} from 'lucide-react';

const PRIMARY = '#c44569';

const formatMoney = (amount, currency = 'PKR') => `${currency} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const getPrimaryCurrency = (currencyTotals = []) => {
  if (!Array.isArray(currencyTotals) || currencyTotals.length === 0) return 'PKR';
  if (currencyTotals.length === 1) return currencyTotals[0].currency || 'PKR';
  return 'MIXED';
};

const StatCard = ({ label, value, sub, Icon, color, bg, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all text-left w-full"
  >
    <div>
      <p className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-2">{label}</p>
      <p className="text-3xl font-extrabold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-2">{sub}</p>}
    </div>
    <div className="w-11 h-11 rounded-xl flex items-center justify-center mt-3" style={{ background: bg }}>
      <Icon size={20} color={color} strokeWidth={2.2} />
    </div>
  </button>
);

const DetailModal = ({ title, rows, columns, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)' }}>
    <div className="bg-white w-full max-w-5xl max-h-[86vh] rounded-2xl shadow-xl border border-slate-100 overflow-hidden animate-[fadeIn_200ms_ease-out]">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 transition">Close</button>
      </div>
      <div className="p-4 overflow-auto max-h-[70vh]">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-100">
              {columns.map((c) => <th key={c.key} className="py-2.5 px-3 font-semibold">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors">
                {columns.map((c) => <td key={`${idx}-${c.key}`} className="py-2.5 px-3 text-slate-700">{c.render ? c.render(r) : (r[c.key] ?? '-')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState({ open: false, title: '', rows: [], columns: [] });

  useEffect(() => {
    adminGetStats()
      .then(r => setStats(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: PRIMARY, borderTopColor: 'transparent' }} />
    </div>
  );

  if (!stats) return <p className="text-slate-500">Failed to load stats.</p>;

  const primaryCurrency = getPrimaryCurrency(stats.currencyTotals || []);

  const openDetail = async (cardKey) => {
    if (cardKey === 'adminUsers') {
      setDetail({
        open: true,
        title: 'Admin Accounts (Recent Summary)',
        rows: (stats.recentUsers || []).filter((u) => u.role === 'admin'),
        columns: [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role' },
          { key: 'created_at', label: 'Created', render: (r) => new Date(r.created_at).toLocaleString() },
        ],
      });
      return;
    }

    if (['totalUsers', 'verifiedUsers', 'blockedUsers'].includes(cardKey)) {
      const res = await adminGetUsers({ page: 1, limit: 100 });
      const allRows = res?.data?.data?.users || [];
      let rows = allRows;
      let title = 'All Users';
      if (cardKey === 'verifiedUsers') {
        rows = allRows.filter((u) => Number(u.email_verified) === 1);
        title = 'Verified Users';
      } else if (cardKey === 'blockedUsers') {
        rows = allRows.filter((u) => Number(u.is_blocked) === 1);
        title = 'Blocked Users';
      }
      setDetail({
        open: true,
        title,
        rows,
        columns: [
          { key: 'id', label: 'ID' },
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'created_at', label: 'Joined', render: (r) => new Date(r.created_at).toLocaleString() },
        ],
      });
      return;
    }

    const txType = cardKey === 'totalRevenue' ? 'receipt' : cardKey === 'totalExpenses' ? 'payment' : 'all';
    const txRes = await adminGetTransactions({ type: txType, limit: 120 });
    const rows = txRes?.data?.data || [];
    setDetail({
      open: true,
      title: txType === 'all' ? 'All Transactions' : txType === 'receipt' ? 'Revenue Transactions' : 'Expense Transactions',
      rows,
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'date', label: 'Date' },
        { key: 'user_name', label: 'User' },
        { key: 'project_name', label: 'Project' },
        { key: 'type', label: 'Type' },
        { key: 'amount', label: 'Amount', render: (r) => formatMoney(r.amount, r.currency || 'PKR') },
        { key: 'description', label: 'Description' },
      ],
    });
  };

  const cards = [
    { key: 'totalUsers', label: 'Total Users',     value: stats.totalUsers,    sub: `+${stats.newToday} today`,     Icon: Users,       color: '#c44569', bg: '#fdf0f4' },
    { key: 'verifiedUsers', label: 'Verified Users',  value: stats.verifiedUsers, sub: `${stats.totalUsers - stats.verifiedUsers} unverified`, Icon: UserCheck,   color: '#10b981', bg: '#d1fae5' },
    { key: 'blockedUsers', label: 'Blocked Users',   value: stats.blockedUsers,  sub: 'accounts restricted',           Icon: UserX,       color: '#ef4444', bg: '#fee2e2' },
    { key: 'adminUsers', label: 'Admin Accounts',  value: stats.adminUsers,    sub: 'with admin role',               Icon: ShieldCheck, color: '#8b5cf6', bg: '#ede9fe' },
    { key: 'totalTransactions', label: 'Total Transactions', value: stats.totalTx,   sub: `${stats.totalReceipts} receipts / ${stats.totalPayments} payments`, Icon: Activity, color: '#f59e0b', bg: '#fef3c7' },
    { key: 'totalRevenue', label: 'Total Revenue',   value: formatMoney(stats.totalRevenue, primaryCurrency),
      sub: 'all-time receipts', Icon: TrendingUp, color: '#10b981', bg: '#d1fae5' },
    { key: 'totalExpenses', label: 'Total Expenses',  value: formatMoney(stats.totalExpenses, primaryCurrency),
      sub: 'all-time payments', Icon: TrendingDown, color: '#ef4444', bg: '#fee2e2' },
    { label: 'Net Balance',
      value: formatMoney(Number(stats.totalRevenue) - Number(stats.totalExpenses), primaryCurrency),
      sub: 'revenue minus expenses', Icon: ArrowUpRight, color: '#c44569', bg: '#fdf0f4' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900">Admin Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Overview of your EMS platform</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map(c => <StatCard key={c.label} {...c} onClick={c.key ? () => openDetail(c.key) : undefined} />)}
      </div>

      {/* Recent rows */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Recent Users */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-800 text-base flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{background:'#fdf0f4',color:PRIMARY}}>
                <Users size={14} strokeWidth={2.5} />
              </div>
              Recent Registrations
            </h2>
          </div>
          <ul className="divide-y divide-slate-50">
            {(stats.recentUsers || []).map(u => (
              <li key={u.id} className="flex items-center gap-3 px-6 py-3 hover:bg-slate-50 transition">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ background: '#fdf0f4', color: PRIMARY }}>
                  {u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
                    style={{ background: u.role === 'admin' ? '#ede9fe' : '#dbeafe', color: u.role === 'admin' ? '#7c3aed' : '#0284c7' }}>
                    {u.role}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(u.created_at).toLocaleDateString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-800 text-base flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{background:'#fdf0f4',color:PRIMARY}}>
                <Activity size={14} strokeWidth={2.5} />
              </div>
              Recent Transactions
            </h2>
          </div>
          <ul className="divide-y divide-slate-50">
            {(stats.recentTx || []).map(t => (
              <li key={t.id} className="flex items-center gap-3 px-6 py-3 hover:bg-slate-50 transition">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: t.type === 'receipt' ? '#d1fae5' : '#fee2e2' }}>
                  {t.type === 'receipt'
                    ? <TrendingUp size={16} color="#10b981" strokeWidth={2.2} />
                    : <TrendingDown size={16} color="#ef4444" strokeWidth={2.2} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.description}</p>
                  <p className="text-xs text-slate-500 truncate">by {t.user_name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold" style={{ color: t.type === 'receipt' ? '#10b981' : '#ef4444' }}>
                    {t.type === 'receipt' ? '+' : '-'}{formatMoney(t.amount, t.currency || 'PKR')}
                  </p>
                  <p className="text-xs text-slate-400">{t.date}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {detail.open && (
        <DetailModal
          title={detail.title}
          rows={detail.rows}
          columns={detail.columns}
          onClose={() => setDetail({ open: false, title: '', rows: [], columns: [] })}
        />
      )}
    </div>
  );
}
